import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { deriveCarrierStatus } from './deriveCarrierStatus';
import { TESTED_CARRIER_VALUES } from './constants';

const EMPTY = Object.freeze({
  byMember: {},
  presentStatesByDisease: {},
  diseaseColorMap: {}
});

// Family diseases = union of every diagnosis present across the pedigree. Each
// diagnosis carries its colour and an `alias` (often an OMIM code) used to match
// gene→disease associations from the terminology service.
function buildFamilyDiseases(members) {
  const byId = new Map();
  for (const m of members || []) {
    for (const dx of m.diagnoses || []) {
      if (!dx || dx.id == null || byId.has(dx.id)) continue;
      byId.set(dx.id, {
        id: dx.id,
        color: dx.color,
        name: dx.name,
        externalIds: [dx.alias, dx.id].filter(Boolean)
      });
    }
  }
  return [...byId.values()];
}

function collectCarrierGenes(members) {
  const genes = new Set();
  for (const m of members || []) {
    for (const g of m.genotypes || []) {
      const status = String((g && g.carrierStatus) || '').trim();
      const sym = String((g && (g.symbol || g.gene)) || '').trim();
      if (sym && TESTED_CARRIER_VALUES.has(status)) genes.add(sym);
    }
  }
  return [...genes].sort();
}

// Projects carrier status onto the pedigree at render time. Fetches the
// gene→disease map (cached per gene) for the family's asserted-carrier genes,
// then runs the pure deriveCarrierStatus projection. Returns EMPTY when the
// feature is disabled so callers can treat "off" uniformly.
export function useCarrierStatus({
  members,
  familyDiseases: familyDiseasesProp,
  enabled = true,
  conditionCarrierEnabled = false
}) {
  const [geneDiseaseMap, setGeneDiseaseMap] = useState({});
  const cacheRef = useRef(new Map()); // geneSymbol -> [diseaseId]

  const genes = useMemo(
    () => (enabled ? collectCarrierGenes(members) : []),
    [members, enabled]
  );
  const genesKey = genes.join(',');

  useEffect(() => {
    if (!enabled || genes.length === 0) {
      setGeneDiseaseMap({});
      return undefined;
    }
    let cancelled = false;
    const missing = genes.filter((g) => !cacheRef.current.has(g));

    (async () => {
      await Promise.all(
        missing.map(async (gene) => {
          try {
            // expand=xrefs folds in curated disease cross-references so a gene
            // (e.g. BRCA1 → OMIM:604370) also matches a family disease recorded
            // under a related code (e.g. OMIM:167000 "Ovarian cancer").
            const url =
              APICONSTANTS.geneDiseaseByGene.replace(
                ':geneSymbol',
                encodeURIComponent(gene)
              ) + '?expand=xrefs';
            const { data } = await axios.get(url);
            cacheRef.current.set(
              gene,
              (data?.diseases || []).map((d) => d.id).filter(Boolean)
            );
          } catch (_) {
            cacheRef.current.set(gene, []); // stale/unavailable → no tested marks
          }
        })
      );
      if (cancelled) return;
      const map = {};
      for (const g of genes) map[g] = cacheRef.current.get(g) || [];
      setGeneDiseaseMap(map);
    })();

    return () => {
      cancelled = true;
    };
  }, [genesKey, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(() => {
    if (!enabled) return EMPTY;
    // The canonical family disease list is authoritative — it carries every tracked disease,
    // including ones nobody is diagnosed with (e.g. ovarian when only the gene is present), with
    // server-assigned colours. But it is gathered from MEMBERS, so a disease that reaches the render
    // by another route resolved no colour and painted '#000'.
    //
    // That is now a UNION rather than an either/or. The case that forced it is a fetus diagnosis
    // (decision 20): it is stored inside a pregnancy record, so the server never sees it, yet
    // deriveReproNodes exposes it on the render node — where the shading pass needs a colour. Since
    // the derived nodes are part of `members` here, picking up what the canonical list is missing
    // covers fetuses without any fetus-specific code, and closes the same hole for anything else.
    // Canonical entries win on conflict, so a server-assigned colour is never overwritten.
    const canonical = (familyDiseasesProp || []).map((d) => ({
      id: d.id,
      color: d.color,
      name: d.name,
      externalIds: [d.alias, d.id].filter(Boolean)
    }));
    const known = new Set(canonical.map((d) => String(d.id)));
    const fromMembers = buildFamilyDiseases(members).filter(
      (d) => !known.has(String(d.id))
    );
    const familyDiseases = fromMembers.length
      ? [...canonical, ...fromMembers]
      : canonical.length
      ? canonical
      : buildFamilyDiseases(members);
    const diseaseColorMap = {};
    for (const d of familyDiseases) {
      diseaseColorMap[d.id] = { color: d.color, name: d.name };
    }
    const { byMember, presentStatesByDisease } = deriveCarrierStatus({
      members,
      familyDiseases,
      geneDiseaseMap,
      conditionCarrierEnabled
    });
    return { byMember, presentStatesByDisease, diseaseColorMap };
  }, [
    members,
    familyDiseasesProp,
    geneDiseaseMap,
    enabled,
    conditionCarrierEnabled
  ]);
}
